import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, NgModule, DEFAULT_CURRENCY_CODE, LOCALE_ID } from '@angular/core';
import { TranslateModule, MissingTranslationHandler } from '@ngx-translate/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OverlayModule } from '@angular/cdk/overlay';
import { InlineSVGModule } from 'ng-inline-svg';
import 'hammerjs';
import { NgxPermissionsModule } from 'ngx-permissions';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { metaReducers, reducers } from './core/reducers';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { ThemeModule } from './views/theme/theme.module';
import { PartialsModule } from './views/partials/partials.module';

import {
    DataTableService,
    KtDialogService,
    LayoutConfigService,
    LayoutRefService,
    MenuAsideService,
    MenuConfigService,
    MenuHorizontalService,
    PageConfigService,
    SplashScreenService,
    SubheaderService,
} from './core/_base/layout';
// CRUD
import { LayoutUtilsService } from './core/_base/crud';
// Config
import { LayoutConfig } from './core/_config/layout.config';
// Highlight JS
import { HighlightModule, HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
// @ts-ignore
import xml from 'highlight.js/lib/languages/xml';
// @ts-ignore
import json from 'highlight.js/lib/languages/json';
// @ts-ignore
import scss from 'highlight.js/lib/languages/scss';
// @ts-ignore
import typescript from 'highlight.js/lib/languages/typescript';
import { registerLocaleData } from '@angular/common';
import ptBr from '@angular/common/locales/pt';
import { OAuthModule } from 'angular-oauth2-oidc';
import { RequestInterceptorService } from './core/_config/request-interceptor.service';
import { CallbackComponent } from './callback/callback.component';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { NotifyComponent } from './components/notify/notify.component';
import { MyMissingTranslationHandler } from './core/_config/missingTranslationHandler';
import { NgbModule, NgbDatepickerI18n, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerModule } from 'ngx-spinner';
import { LoaderService } from './core/_config/loader.service';
import { CustomDatepickerI18n, I18n } from './components/datepickeri18n/customDatePIckerI18n';
import { NgbDatePTParserFormatter } from './components/datepickeri18n/NgbDatePTParserFormatter';
import { InitJsonConfig, JsonConfig } from './core/_config/json.config';
import { AppConfig } from './core/_config/app.config';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlCro } from './core/_config/translatePaginator';
import { RedirectGuard } from './core/_config/redirect-guard';
import { LocaleService } from './core/_base/layout/services/locale.service';
import { CURRENCY_MASK_CONFIG } from 'ngx-currency';
import { CountryContextService } from './core/_base/layout/services/countryContext.service';
import { BrowserModule } from '@angular/platform-browser';
import { AccountingModule } from './views/pages/accounting/accounting.module';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { BrazilMatDateFormatExteion } from './core/extension/BrazilMatDateFormatExteion';

export function initializeLayoutConfig(appConfig: LayoutConfigService) {
    // initialize app   by loading default demo layout config
    return () => {
        if (appConfig.getConfig() === null) {
            appConfig.loadConfigs(new LayoutConfig().configs);
        }
    };
}

/**
 * Import specific languages to avoid importing everything
 * The following will lazy load highlight.js core script (~9.6KB) + the selected languages bundle (each lang. ~1kb)
 */
export function getHighlightLanguages() {
    return [
        { name: 'typescript', func: typescript },
        { name: 'scss', func: scss },
        { name: 'xml', func: xml },
        { name: 'json', func: json },
    ];
}

registerLocaleData(ptBr);

@NgModule({
    declarations: [AppComponent, CallbackComponent, NotifyComponent],
    imports: [
        CommonModule,
        BrowserModule,
        NgbModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        HttpClientModule,
        NgxPermissionsModule.forRoot(),
        HighlightModule,
        PartialsModule,
        CoreModule,
        OverlayModule,
        StoreModule.forRoot(reducers, { metaReducers }),
        EffectsModule.forRoot([]),
        StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
        StoreDevtoolsModule.instrument(),
        TranslateModule.forRoot({
            missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
            useDefaultLang: false,
        }),

        MatProgressSpinnerModule,
        InlineSVGModule.forRoot(),
        ThemeModule,
        OAuthModule.forRoot(),
        SimpleNotificationsModule.forRoot(),
        NgxSpinnerModule,
        AccountingModule,
    ],
    schemas: [],
    exports: [],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' }, // garante locale brasileiro
        { provide: MAT_DATE_FORMATS, useValue: BrazilMatDateFormatExteion },
        {
            // layout config initializer
            provide: APP_INITIALIZER,
            useFactory: InitJsonConfig,
            deps: [JsonConfig],
            multi: true,
        },
        {
            provide: AppConfig,
            useExisting: JsonConfig,
        },
        LayoutConfigService,
        CountryContextService,
        LayoutRefService,
        MenuConfigService,
        PageConfigService,
        KtDialogService,
        DataTableService,
        SplashScreenService,
        LoaderService,
        {
            provide: LOCALE_ID,
            deps: [LocaleService],
            useFactory: (localeService: LocaleService) => localeService.initLanguage(),
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            deps: [LocaleService],
            useFactory: (localeService: LocaleService) => localeService.getCurrencySelected,
        },
        {
            provide: CURRENCY_MASK_CONFIG,
            deps: [LocaleService],
            useFactory: (localeService: LocaleService) => localeService.getCurrencyMaskConfig,
        },
        {
            // layout config initializer
            provide: APP_INITIALIZER,
            useFactory: initializeLayoutConfig,
            deps: [LayoutConfigService],
            multi: true,
        },

        { provide: MatPaginatorIntl, useClass: MatPaginatorIntlCro },
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                languages: getHighlightLanguages,
            },
        },
        // template services
        SubheaderService,
        MenuHorizontalService,
        MenuAsideService,
        LayoutUtilsService,
        { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptorService, multi: true },
        [I18n, { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }],
        [{ provide: NgbDateParserFormatter, useClass: NgbDatePTParserFormatter }],
        RedirectGuard,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
